/**
 * A cache that stores async values for a key.
 * It ensures that once a promise for a value is put the subsequent calls to get
 * for the same key will wait for this promise.
 * The maxSize is only for resolved values.
 */
export default class DataCache {

  constructor(maxSize) {
    this.maxSize = maxSize;
    this.data = {};
    this.awaitData = {};
    this.keys = [];
  }

  put(key, dataPromise) {
    dataPromise.then((value) => {
      this.putValue(key, value);
      delete this.awaitData[key];
    }).catch(() => {
      /* There will simply be no value in the cache */
      delete this.awaitData[key];
    });
    this.awaitData[key] = dataPromise;
  }

  putValue(key, value) {
    this.data[key] = value;
    const keyIndex = this.keys.indexOf(key);
    if (keyIndex >= 0) {
      this.keys.splice(keyIndex, 1);
    } else if (this.keys.length >= this.maxSize) {
      const removeKey = this.keys.shift();
      delete this.data[removeKey];
    }
    this.keys.push(key);
  }

  get(key) {
    const waitingData = this.awaitData[key];
    if (waitingData) {
      return waitingData;
    }

    const keyIndex = this.keys.indexOf(key);
    if (keyIndex >= 0) {
      this.keys.splice(keyIndex, 1);
      this.keys.push(key);
    }
    return this.data[key];
  }

  /**
   * Will return the cached value or call the loader function to initialize the value.
   * @param key
   * @param loaderFunction The function that will will be called to return a promise
   *                       with the data if the key is not in the cached
   */
  getOrLoad(key, loaderFunction) {
    const result = this.get(key);
    if (result) {
      return result;
    }
    const waitingData = loaderFunction();
    this.put(key, waitingData);
    return waitingData;
  }
}
